/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { MenuItem } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ReactHookFormSelect } from '@opengeoweb/form-fields';
import ProductFormFieldLayout from './ProductFormFieldLayout';
import { useShortTestHelpers } from '../../../hooks/useShortTestHelpers';
import type {
  AviationPhenomenon,
  ConfigurableFormFieldProps,
} from '../../../types';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import { getFieldLabel, triggerValidations } from '../utils';

const Phenomenon: React.FC<
  ConfigurableFormFieldProps & { phenomenaList: AviationPhenomenon[] }
> = ({
  isDisabled,
  isReadOnly,
  onChange = (): void => {},
  phenomenaList,
}: ConfigurableFormFieldProps & { phenomenaList: AviationPhenomenon[] }) => {
  const { t } = useSigmetAirmetTranslation();
  const { getValues, setValue, trigger } = useFormContext();
  const { isShortTest } = useShortTestHelpers();
  const label = getFieldLabel(t, t('phenomenon'), isReadOnly!);
  const fieldsDisabled = isDisabled || isShortTest();
  const isRequired = (value: string): string | boolean =>
    isShortTest() ? true : !!value;

  const handleSinglePhenomenaSelection = (): void => {
    const defaultPhenomenon = getValues('phenomenon');
    const isValidPhenomenon = phenomenaList.some(
      (phen) => phen.code === defaultPhenomenon,
    );

    // Only auto-select if there is exactly one phenomenon available
    if (!isValidPhenomenon && phenomenaList.length === 1) {
      setValue('phenomenon', phenomenaList[0]?.code || '');
    }
  };

  if (phenomenaList.length === 1) {
    handleSinglePhenomenaSelection();
  }

  return (
    <ProductFormFieldLayout
      sx={{
        '.MuiFormHelperText-root.Mui-error': {
          marginBottom: '-22px',
          marginTop: '2px',
        },
      }}
      title={t('phenomenon-title')}
    >
      <ReactHookFormSelect
        data-testid="phenomenon"
        disabled={fieldsDisabled}
        isReadOnly={isReadOnly}
        label={label}
        name="phenomenon"
        onChange={(): void => {
          if (getValues('movementType') === 'NO_VA_EXP') {
            setValue('movementType', null);
          } else if (getValues('phenomenon') === 'RDOACT_CLD') {
            // By definition, phenomenon 'RDOACT_CLD'shall not ever move but stay stationary
            setValue('movementType', 'STATIONARY');

            // Also by definition, phenomenon 'RDOACT_CLD' is strictly defined to always span from surface to upper level
            // with no lower explicit level values defined, hence level mode shall be set to 'BETW_SFC' instead of 'BETW'.
            setValue('levelInfoMode', 'BETW_SFC');
          }

          triggerValidations(
            ['validDateStart', 'validDateEnd'],
            getValues,
            trigger,
          );
          onChange();
        }}
        rules={{
          validate: {
            required: isRequired,
          },
        }}
        size="small"
      >
        {phenomenaList.map((phenomenon) => (
          <MenuItem key={phenomenon.code} value={phenomenon.code}>
            {phenomenon.description}
          </MenuItem>
        ))}
      </ReactHookFormSelect>
    </ProductFormFieldLayout>
  );
};

export default Phenomenon;
